import {
  Component, EventEmitter, Output, ViewEncapsulation, OnInit, OnDestroy, inject, HostListener,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule, FormBuilder, Validators, ValidatorFn, AbstractControl, ValidationErrors,
} from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { TranslatePipe } from '@ngx-translate/core';
import { API_BASE_URL } from '../../core/api-base-url.token';
import { LanguageService } from '../../core/services/language.service';
import { LanguageSelectComponent } from '../language-select/language-select.component';

type MeResponse = {
  user: { firstName: string; lastName: string; email: string; locale?: string | null };
};

function passwordPolicyValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const v = (control.value ?? '') as string;
    if (!v || v.trim().length === 0) return null; // MDP optionnel
    const hasLen = v.length >= 12;
    const hasLower = /[a-z]/.test(v);
    const hasUpper = /[A-Z]/.test(v);
    const hasDigit = /\d/.test(v);
    const hasSpecial = /[^A-Za-z0-9]/.test(v);
    const noSpaces = !/\s/.test(v);
    const ok = hasLen && hasLower && hasUpper && hasDigit && hasSpecial && noSpaces;
    return ok ? null : { weakPassword: true };
  };
}

@Component({
  selector: 'app-account-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, LanguageSelectComponent, TranslatePipe],
  templateUrl: './account-modal.component.html',
  styleUrls: ['./account-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None,
})
export class AccountModalComponent implements OnInit, OnDestroy {
  @Output() close = new EventEmitter<void>();

  private http = inject(HttpClient);
  private fb = inject(FormBuilder);
  private languageService = inject(LanguageService);

  /** Base d’URL injectée dynamiquement (local/dev/prod) */
  private base = inject(API_BASE_URL);

  // Endpoints API
  private meUrl     = `${this.base}/api/v1/auth/me`;   // GET
  private updateUrl = `${this.base}/api/v1/users/me`;  // PUT

  loading = false;
  errorMsg = '';
  okMsg = '';

  passwordMatchValidator = (control: AbstractControl): ValidationErrors | null => {
    const newPassword = control.get('newPassword')?.value;
    const confirmPassword = control.get('confirmPassword')?.value;
    
    if (!newPassword || newPassword.trim().length === 0) {
      return null;
    }
    
    return newPassword === confirmPassword ? null : { passwordMismatch: true };
  };

  form = this.fb.group({
    firstName: ['', Validators.required],
    lastName:  ['', Validators.required],
    email:     [{ value: '', disabled: true }, [Validators.required, Validators.email]],
    locale:    [this.languageService.currentLang()],
    newPassword: ['', passwordPolicyValidator()],
    confirmPassword: [''],
  }, { validators: this.passwordMatchValidator });

  ngOnInit(): void {
    document.body.classList.add('modal-open');
    this.loadMe();
  }

  ngOnDestroy(): void {
    document.body.classList.remove('modal-open');
  }

  private loadMe(): void {
    this.loading = true;
    this.errorMsg = '';
    this.http.get<MeResponse>(this.meUrl).subscribe({
      next: (res) => {
        const u = res?.user;
        this.form.patchValue({
          firstName: u?.firstName ?? '',
          lastName : u?.lastName ?? '',
          email    : u?.email ?? '',
          locale   : u?.locale ?? this.languageService.currentLang(),
        });
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.errorMsg =
          err?.status === 401
            ? 'Session expirée — veuillez vous reconnecter.'
            : 'Impossible de charger le profil.';
      },
    });
  }

  // Checklist live pour l’UI
  get pw() { return this.form.get('newPassword'); }
  get pwValue(): string { return (this.pw?.value ?? '') as string; }
  get pwRules() {
    const v = this.pwValue;
    return {
      len: v.length >= 8,
      lower: /[a-z]/.test(v),
      upper: /[A-Z]/.test(v),
      digit: /\d/.test(v),
      special: /[^A-Za-z0-9]/.test(v),
      spaces: !/\s/.test(v),
      empty: v.trim().length === 0,
    };
  }



  onSubmit(): void {
    if (this.form.invalid || this.loading) return;

    this.loading = true;
    this.errorMsg = '';
    this.okMsg = '';

    const raw = this.form.getRawValue();
    const payload: any = {
      first_name: (raw.firstName ?? '').trim(),
      last_name : (raw.lastName ?? '').trim(),
      locale    : raw.locale,
    };

    const pwd = (raw.newPassword ?? '').trim();
    if (pwd.length > 0) {
      payload.new_password = pwd; // validé côté front + back
    }

    this.http.put(this.updateUrl, payload).subscribe({
      next: () => {
        this.loading = false;
        this.okMsg = 'Profil mis à jour.';
        this.languageService.setLang(payload.locale, false); // déjà persisté ci-dessus, pas de double PUT
        this.closeModal(); // ferme après succès
      },
      error: (err) => {
        this.loading = false;
        const apiMsg =
          err?.error?.message ||
          err?.error?.detail ||
          (typeof err?.error === 'string' ? err.error : null);
        this.errorMsg =
          err?.status === 401
            ? 'Session expirée — veuillez vous reconnecter.'
            : apiMsg || "Échec de l'enregistrement.";
      },
    });
  }

  /** Fermer via bouton Annuler ou clic backdrop */
  closeModal(): void {
    if (this.loading) return; // on évite de fermer en plein submit
    this.close.emit();
  }

  /** Fermer avec la touche Échap */
  @HostListener('document:keydown.escape')
  onEsc(): void {
    this.closeModal();
  }
}
